import { Alert, Box, Button, TextArea, TextInput, useToast } from '@razorpay/blade/components';
import api from 'api';
import { getErrorsAsString } from 'components/Kyc/utils';
import { routePaths } from 'components/Routes/data';
import { useComponentParams } from 'components/SalaryComponents/hooks';
import { NonPayableBenefitsFormData } from 'components/WizardViews/SalaryComponents/types';
import { convertFormDataToNonPayableBenefitsSalaryComponentUpdatePayload } from 'components/WizardViews/SalaryComponents/utils';
import {
  FieldGroup,
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
} from 'components/WizardViews/components';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { ValidationSchema, useForm } from 'forms';
import { useEffect, useMemo } from 'react';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { AppError } from 'utils/AppError';
import { FullScreenLoader } from 'components/ui/FullScreenLoader';

// Validation schema for edit mode
const editValidationSchema: ValidationSchema<NonPayableBenefitsFormData> = {
  displayName: (formData) => {
    if (!formData.displayName) return 'Display name is required';
    return null;
  },
  description: () => null,
};

interface GeneralDetailsStepProps {
  formData: NonPayableBenefitsFormData | null;
  setFormData: (data: NonPayableBenefitsFormData) => void;
  isError?: boolean;
  onRefresh?: () => void;
  isLoading?: boolean;
}

const GeneralDetailsStep = ({
  formData: propFormData,
  setFormData: propSetFormData,
  isError = false,
  isLoading = false,
  onRefresh = () => {},
}: GeneralDetailsStepProps) => {
  const { formData, validate, register, setFormData } = useForm<NonPayableBenefitsFormData>();
  const history = useHistory();
  const toast = useToast();
  const { componentId } = useComponentParams();

  // Update local form data when prop data changes
  useEffect(() => {
    if (propFormData && !formData?.name) {
      setFormData(propFormData);
    }
  }, [propFormData, formData, setFormData]);

  const updateNonPayableBenefits = useMutation({
    mutationFn: api.salaryComponents.mutations.updateSalaryComponent,
    onError: (e: AppError) => {
      toast.show({
        content: 'There was an error in updating salary component.',
        color: 'negative',
        duration: 3000,
      });
    },
    onSuccess() {
      toast.show({
        content: 'Salary component updated successfully!',
        color: 'positive',
        duration: 3000,
      });
      history.push(routePaths.salaryComponents.nonPayableBenefitsListView);
    },
  });

  const isFormValid = useMemo(
    () => validate(editValidationSchema, { sideEffects: false }),
    [validate],
  );

  const handleSubmit = () => {
    if (componentId) {
      updateNonPayableBenefits.mutate(
        convertFormDataToNonPayableBenefitsSalaryComponentUpdatePayload(formData, componentId),
      );
      history.push({
        pathname: routePaths.salaryComponents.nonPayableBenefitsListView,
      });
    }
  };

  // Don't render until we have data
  if (!propFormData || !formData) {
    return null;
  }

  const alertDescription = (
    <>
      If display name is updated, past salary registers and payslips will reflect the new Display
      Name.
      <br />
    </>
  );

  if (isError) {
    return (
      <Box
        height={'100%'}
        backgroundColor={'surface.background.gray.intense'}
        alignItems={'center'}
        justifyContent={'center'}
        display={'flex'}>
        <EmptyListView
          title="This page could not be loaded due to unknown error. "
          description=""
          isError={true}>
          <Box display={'flex'} flexDirection={'row'} gap={'spacing.4'} marginX={'auto'}>
            <Button
              size="medium"
              onClick={() => {
                onRefresh?.();
              }}>
              Retry
            </Button>
            <Button
              size="medium"
              onClick={() => {
                history.push(routePaths.salaryComponents.nonPayableBenefitsListView);
              }}>
              Contact support
            </Button>
          </Box>
        </EmptyListView>
      </Box>
    );
  }

  if (isLoading) {
    return <FullScreenLoader accessibilityLabel="loading non-payable benefits components" />;
  }

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper>
        <StepHeader title="General" subtitle="Configuration" marginBottom={'spacing.9'} />

        {updateNonPayableBenefits.isError && (
          <Alert
            marginBottom="spacing.7"
            title="The component cannot be updated because of the below errors:"
            description={getErrorsAsString(updateNonPayableBenefits.error)}
            color="negative"
            isFullWidth
            isDismissible={false}
          />
        )}

        <Alert
          marginBottom="spacing.7"
          isFullWidth
          title="Edit details"
          isDismissible={false}
          description={alertDescription}
        />

        <FieldGroup title="General details">
          <TextInput
            label="Component name"
            placeholder="Enter component name"
            {...register('name')}
            isDisabled
          />

          <TextInput
            label="Display name in payslip & Salary Register"
            placeholder="Enter display name"
            helpText="Display will be used in employee payslip and salary register"
            {...register('displayName')}
            isRequired
          />

          <TextArea
            label="Component description"
            placeholder="Enter description"
            {...register('description')}
          />
        </FieldGroup>
      </StepContentWrapper>

      <StepActionsWrapper>
        <span />
        <Button
          isLoading={updateNonPayableBenefits.isLoading}
          type="submit"
          isDisabled={!isFormValid}>
          Confirm changes
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default GeneralDetailsStep;
